import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Input, Radio, Space, Switch, Typography, message } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import {
  getMeituanConfig,
  saveMeituanConfig,
  type MeituanConfig as MeituanConfigValues,
} from '../services/meituanConfigService';

const { Title, Paragraph, Text } = Typography;

const defaultValues: MeituanConfigValues = {
  enabled: false,
  environment: 'production',
  developerAccount: '',
  appSecret: '',
  shopAuthUrl: '',
  remark: '',
};

const MeituanConfig: React.FC = () => {
  const [form] = Form.useForm<MeituanConfigValues>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadConfig = async () => {
      setLoading(true);
      try {
        const data = await getMeituanConfig();
        form.setFieldsValue({ ...defaultValues, ...data });
      } catch {
        message.error('加载美团配置失败');
        form.setFieldsValue(defaultValues);
      } finally {
        setLoading(false);
      }
    };

    void loadConfig();
  }, [form]);

  const handleSubmit = async (values: MeituanConfigValues) => {
    setSaving(true);
    try {
      const data = await saveMeituanConfig({
        ...defaultValues,
        ...values,
        enabled: Boolean(values.enabled),
      });
      form.setFieldsValue({ ...defaultValues, ...data });
      message.success('保存成功');
    } catch {
      message.error('保存美团配置失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <Title level={2}>美团配置</Title>
      </div>

      <div className="page-content">
        <Alert
          style={{ marginBottom: 16 }}
          type="info"
          showIcon
          message="用于保存美团开放平台接入参数"
          description="启用前请确认开发者账号和 AppSecret 已填写。商户ID、门店ID、回调地址在美团后台或对应业务模块单独管理。"
        />

        <Card title="开发者账号配置" loading={loading}>
          <Form
            form={form}
            layout="vertical"
            initialValues={defaultValues}
            onFinish={handleSubmit}
          >
            <Form.Item name="enabled" label="启用状态" valuePropName="checked">
              <Switch checkedChildren="启用" unCheckedChildren="停用" />
            </Form.Item>

            <Form.Item name="environment" label="接口环境" rules={[{ required: true, message: '请选择接口环境' }]}>
              <Radio.Group>
                <Radio.Button value="production">正式环境</Radio.Button>
                <Radio.Button value="sandbox">沙箱环境</Radio.Button>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              name="developerAccount"
              label="美团开发者账号"
              rules={[{ max: 128, message: '开发者账号长度不能超过 128 个字符' }]}
            >
              <Input placeholder="请输入美团开放平台开发者账号" />
            </Form.Item>

            <Form.Item
              name="appSecret"
              label="AppSecret"
              rules={[{ max: 255, message: 'AppSecret 长度不能超过 255 个字符' }]}
            >
              <Input.Password placeholder="请输入美团 AppSecret" autoComplete="new-password" />
            </Form.Item>

            <Form.Item name="remark" label="备注" rules={[{ max: 1000, message: '备注长度不能超过 1000 个字符' }]}>
              <Input.TextArea rows={4} placeholder="记录应用用途、门店范围、接入说明等" />
            </Form.Item>
          </Form>
        </Card>

        <Card title="操作" style={{ marginTop: 16 }}>
          <Space orientation="vertical" size={8}>
            <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={() => form.submit()}>
              保存配置
            </Button>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              <Text strong>说明：</Text>
              当前页面只负责保存美团开发者配置，具体美团业务 API 调用会基于这些参数继续开发。
            </Paragraph>
          </Space>
        </Card>
      </div>
    </div>
  );
};

export default MeituanConfig;
